import React, { useState, useEffect } from 'react';
import { Box, IconButton, useTheme, Button, Pagination, Typography, Tooltip } from "@mui/material";
import { Autorenew, Delete, EditOutlined } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { tokens } from 'shared/theme';
import { useNavigate, useParams } from "react-router-dom";
import useUserService from 'shared/api/services/userService';
import Table from 'shared/components/Table';
import Header from "shared/components/Header";
import LoadingSpinner from 'shared/components/LoadingSpinner';
import CreateUserDialog from './CreateUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import { buildUserDetailsPath } from 'shared/utils/tenantRoute';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';

const UserTable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const { users, totalRecords, fetchUsers, deleteUser, rotateUserStatus, isLoading } = useUserService(); 
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showAllUsers, setShowAllUsers] = useState(false);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserStatus, setSelectedUserStatus] = useState(null);

  useEffect(() => {
    fetchUsers(page, size, canViewAll && showAllUsers ? null : "ACTIVE");
  }, [page, showAllUsers, canViewAll]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleCreateClick = () => {
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
    fetchUsers(page, size, showAllUsers ? null : "ACTIVE");
  };

  const handleDeleteClick = (userId, status) => {
    setSelectedUserId(userId);
    setSelectedUserStatus(status);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUserStatus(null);
  };

  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUserStatus(null);
    fetchUsers(page, size, showAllUsers ? null : "ACTIVE");
  };

  const handleRotateStatus = async (userId) => {
    try {
      await rotateUserStatus(userId, () => {
        fetchUsers(page, size, showAllUsers ? null : "ACTIVE");
      });
    } catch (error) {
      // Error is already handled in the service with toast
    }
  };

  const openUserDetailsPage = (selectedUserId) => {
    const registryTenantId = getRegistryTenantIdFromSession();
    navigate(buildUserDetailsPath(selectedUserId, registryTenantId));
  };

  const handleAllUsersClick = () => {
    if (!canViewAll) return;
    setShowAllUsers(true);
    fetchUsers(page, size, null);
  };

  const columns = [
    { field: "userName", headerName: "UserName", flex: 1 , minWidth : 150 },
    { field: "phoneNumber", headerName: "Phone Number", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "communityName", headerName: "communityName", flex: 1 },
    { field: "parishName", headerName: "parishName", flex: 1 },
    { field: "archdiocese", headerName: "archdiocese", flex: 1 },
    { field: "createdAt", headerName: "createdAt", flex: 1 },
    { field: "role", headerName: "Role", flex: 1 , minWidth : 100 },
    { field: "status", headerName: "Status", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" gap={0.5}>
          <Tooltip title="Rotate Status (ACTIVE → DISABLED → DELETED → ACTIVE)">
            <IconButton color="secondary" onClick={() => handleRotateStatus(row.id)}>
              <RotateRightIcon style={{ color: "red" }} />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title={row.status === 'ACTIVE' ? 'Disable User' : 'Enable User'}>
            <IconButton color="secondary" onClick={() => handleDeleteClick(row.id, row.status)}>
              <Delete style={{ color: "red" }} />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="View User Details">
            <IconButton color="success" onClick={() => openUserDetailsPage(row.id)}>
              <VisibilityOutlinedIcon style={{ color: "green" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="0px">
      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        userId={selectedUserId}
        currentStatus={selectedUserStatus}
        onSuccess={handleDeleteSuccess}
      />

      <CreateUserDialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        onSuccess={handleCreateSuccess}
      />

      

      <Box p={2} ml={2} mr={2}>
        <Header title="Users" />
        <Box>
          {canViewAll ? (
          <Button
            color="primary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px" }}
            onClick={handleAllUsersClick}
          >
            View all
          </Button>
          ) : null}
          <Button
            color="primary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={handleCreateClick}
          >
            Add user
          </Button>
        </Box>

        {isLoading ? (
          <LoadingSpinner message="Loading Users..." height="300px" />
        ) : (
          <Table
            tableId="platform.users"
            title="Users"
            rows={users}
            columns={columns}
            totalRecords={totalRecords}
            size={size}
            page={page}
            handlePageChange={handlePageChange}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
};

export default UserTable;